<script setup>
import { ref, watch, computed, nextTick } from "vue";
import Row from "./Row.vue";
import _cloneDeep from "lodash/cloneDeep";
import _uniqueId from "lodash/uniqueId";
import _concat from "lodash/concat";
import { insertAt, removeAt, textToRows, treeToRows, rowsToTree, createRow } from "./utils";
import _isNumber from "lodash/isNumber";
import _merge from "lodash/merge";
import LoadingAnimation from "@Components/Animation/Loading";
import { InformationCircleIcon } from "@heroicons/vue/20/solid";
import ActionButton from "./ActionButton.vue";
import Action from "./Actions/Action";
import { useI18n } from "vue-i18n";
import { FormTextarea } from "@Components/Form";

const props = defineProps({
    modelValue: {
        type: Array,
        default: [],
    },
    depthEnabled: {
        type: Boolean,
        default: false,
    },
    sortableEnabled: {
        type: Boolean,
        default: false,
    },
    singleMode: {
        type: Boolean,
        default: false,
    },
    actions: {
        type: Object,
        default: () => {},
    },
    readonly: {
        type: Boolean,
        default: false,
    },
    disableNewRows: {
        type: Boolean,
        default: false,
    },
});

const { t: __ } = useI18n();

const emit = defineEmits(["update:modelValue"]);

const isFocused = ref(false);

const isSimpleMode = computed(() => {
    return props.singleMode;
});

const getRowAt = (index) => {
    return rows.value[index];
};

const getElementAt = (index) => {
    return rowRefs.value[index].querySelector("[value-input]");
};

const fallbackKeyPressed = (event, index) => {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;

    if (event.key === "ArrowUp") {
        if (index > 0) {
            focusRow(index - 1, "end");
        }
    } else {
        if (index < props.modelValue.length - 1) {
            focusRow(index + 1, "end");
        }
    }
};

// handler for enter key
// if the cursor selection is at end
// add a new empty row
// else, split the value between cursor
// and add a new row with first part before the row
// and update the current row with second part
const enterKeyPressed = async (index) => {
    if (props.readonly || props.disableNewRows) {
        return;
    }

    const row = getRowAt(index);
    const el = getElementAt(index);

    let indexToFocus = index + 1;

    // get input selection start
    const selectionStart = el.selectionStart;

    if (selectionStart >= getRowAt(index).title.length) {
        // insert a new empty row at index + 1
        rows.value = insertAt(
            _cloneDeep(rows.value),
            [
                createRow({
                    depth: row.depth,
                }),
            ],
            index + 1
        );
    } else {
        let prevInputValue = row.title.slice(0, selectionStart);

        let nextInputValue = row.title.slice(selectionStart);

        // insert a new row at index
        rows.value = insertAt(
            _cloneDeep(rows.value),
            [
                createRow({
                    title: prevInputValue,
                    depth: row.depth,
                }),
            ],
            index
        );

        // update current row value
        rows.value[index + 1].title = nextInputValue;

        // if the prevInputValue is empty, focus it
        if (prevInputValue.length === 0) {
            indexToFocus = index;
        }
    }

    // fix rows
    fixRows();

    await nextTick();

    focusRow(indexToFocus, "start");
};

// handler for delete key
// if the value is empty, remove the row and focus the parent
// else if selector is at start, copy the title at the and of the parent title
const deleteKeyPressed = async (event, index) => {
    if (props.readonly) {
        return;
    }

    const row = getRowAt(index);
    const el = getElementAt(index);

    // if empty
    if (row.title.length === 0) {
        // remove rows if is not the last row
        if (rows.value.length > 1) {
            event.preventDefault();

            rows.value = removeAt(_cloneDeep(rows.value), index);

            // fix rows
            fixRows();

            // wait next tick for focus input
            await nextTick();

            // focus the parent row
            if (index > 0) {
                focusRow(index - 1, "end");
            }
        }
    } else {
        // get input selection start
        const selectionStart = el.selectionStart;
        const selectionEnd = el.selectionEnd;
        if (selectionStart === 0 && selectionEnd === 0 && index > 0) {
            event.preventDefault();

            // save parent row value length to futur focus
            const parentTitleLength = rows.value[index - 1].title.length;

            // update parent row value
            rows.value[index - 1].title = rows.value[index - 1].title + row.title;

            // remove current row
            rows.value = removeAt(_cloneDeep(rows.value), index);

            // fix rows
            fixRows();

            // wait next tick for focus input
            await nextTick();

            // focus the parent row
            focusRow(index - 1, parentTitleLength);
        }
    }
};

// handler for exact tab key
const tabKeyPressed = async (index) => {
    if (props.readonly) {
        return;
    }

    if (index <= 0) {
        return;
    }

    // get previous row to determine max depth
    const previousRow = getRowAt(index - 1);

    // update the current row depth
    rows.value[index].depth = Math.min(rows.value[index].depth + 1, previousRow.depth + 1);

    // fix rows
    fixRows();
};

// handler for shifted tab key
const shiftedTabKeyPressed = async (index) => {
    if (props.readonly) {
        return;
    }

    // update the current row depth
    rows.value[index].depth = Math.max(rows.value[index].depth - 1, 0);

    // fix rows
    fixRows();
};

const removeRow = async (index) => {
    rows.value = removeAt(_cloneDeep(rows.value), index);

    // fix rows
    fixRows();

    // wait next tick for focus input
    await nextTick();
};

// handler for paste
const pastePressed = async (event, index) => {
    if (props.readonly) {
        return;
    }

    // get pasted text
    let paste = (event.clipboardData || window.clipboardData).getData("text");

    let rowsToInsert = textToRows(paste);

    // insert first row at current index
    const row = getRowAt(index);
    const el = getElementAt(index);
    const selectionStart = el.selectionStart;
    const selectionEnd = el.selectionEnd;

    let prevInputValue = row.title.slice(0, selectionStart);
    let nextInputValue = row.title.slice(selectionEnd);

    let titleToInsert = rowsToInsert.shift().title;
    rows.value[index].title = prevInputValue + titleToInsert + nextInputValue;
    let indexToFocus = index;
    let lengthToFocus = selectionStart + titleToInsert.length;

    // insert other pasted rows
    if (rowsToInsert.length > 0 && !props.disableNewRows) {
        rows.value = insertAt(_cloneDeep(rows.value), rowsToInsert, index + 1);
        indexToFocus = index + 1;
        lengthToFocus = "end";
    }

    // remove current row if empty
    if (getRowAt(index).title.length === 0) {
        rows.value = removeAt(_cloneDeep(rows.value), index);
    }

    // fix rows
    fixRows();

    await nextTick();

    focusRow(indexToFocus, lengthToFocus);
};

const upKeyPressed = async (index) => {
    const row = getRowAt(index);
    const rowElement = getElementAt(index);
    if (index === 0) {
        focusRow(index, "start");
    } else {
        const previousRow = getRowAt(index - 1);

        const selectionStart = Math.max(0, rowElement.selectionStart - (previousRow.depth - row.depth) * 4);

        focusRow(index - 1, selectionStart);
    }
};

const downKeyPressed = async (index) => {
    const row = getRowAt(index);
    const rowElement = getElementAt(index);

    if (index === rows.value.length - 1) {
        focusRow(index, "end");
    } else {
        const nextRow = getRowAt(index + 1);

        const selectionStart = Math.max(0, rowElement.selectionStart - (nextRow.depth - row.depth) * 4);

        focusRow(index + 1, selectionStart);
    }
};

const moveUpPressed = async (index) => {
    if (index > 0) {
        const row = _cloneDeep(getRowAt(index));
        const previousRow = getRowAt(index - 1);
        row.depth = previousRow.depth;
        rows.value = removeAt(_cloneDeep(rows.value), index);
        rows.value = insertAt(_cloneDeep(rows.value), row, index - 1);

        // fix rows
        fixRows();
    }
};

const moveDownPressed = async (index) => {
    if (index < rows.value.length - (props.disableNewRows ? 1 : 2)) {
        const row = _cloneDeep(getRowAt(index));
        const nextRow = getRowAt(index + 1);
        row.depth = nextRow.depth;
        rows.value = removeAt(_cloneDeep(rows.value), index);
        rows.value = insertAt(_cloneDeep(rows.value), row, index + 1);

        // fix rows
        fixRows();
    }
};

// focus input element at index
// use at parameter to define cursor position
// start or end
const focusRow = (index, at) => {
    const el = getElementAt(index);
    el.focus();
    if (at === "start") {
        el.setSelectionRange(0, 0);
    } else if (at === "end") {
        el.setSelectionRange(el.value.length, el.value.length);
    } else if (_isNumber(at)) {
        el.setSelectionRange(at, at);
    }
};

// fix rows
const fixRows = () => {
    // add row if rows is empty
    if (!isSimpleMode.value && rows.value.length === 0 && !props.readonly && !props.disableNewRows) {
        rows.value = insertAt(_cloneDeep(rows.value), [createRow()], 0);
    }

    if (rows.value.length === 0) {
        return;
    }

    // fix depth if a issue is find
    rows.value[0].depth = 0;
    for (let i = 1; i < rows.value.length; i++) {
        if (rows.value[i].depth === undefined) {
            rows.value[i].depth = 0;
        } else {
            rows.value[i].depth = Math.min(rows.value[i].depth, rows.value[i - 1].depth + 1);
        }
    }

    if (!props.depthEnabled) {
        for (let i = 1; i < rows.value.length; i++) {
            rows.value[i].depth = 0;
        }
    }

    // add an empty row at end
    if (!isSimpleMode.value && !props.readonly && !props.disableNewRows && rows.value[rows.value.length - 1].title.length > 0) {
        rows.value = insertAt(_cloneDeep(rows.value), [createRow()], rows.value.length);
    }
};

const rows = ref(treeToRows(props.modelValue));
const rowRefs = ref([]);
const loading = ref(false);

const _headerActions = props.actions.header
    ? props.actions.header.map((options) => {
          return new Action(options);
      })
    : [];

const _footerActions = props.actions.footer
    ? props.actions.footer.map((options) => {
          return new Action(options);
      })
    : [];

watch(
    rows,
    (newRows) => {
        fixRows();
        emit("update:modelValue", rowsToTree(_cloneDeep(newRows)));
    },
    { deep: true, immediate: true }
);

// const runAction = async (action) => {
//     await action.callback(rows.value.length - 2);
// };

const runAction = (action) => {
    action.run();
};

const addRowsAt = (els, index) => {
    rows.value = insertAt(_cloneDeep(rows.value), els, index);
};

const replaceRows = (els) => {
    rows.value = insertAt([], els, 0);
};

const updateRow = (index, row) => {
    rowRefs.value[index].updateTitle(row.title);
    // rows.value[index] = row;
};

const getRows = () => {
    return _cloneDeep(rows.value);
};

const setLoading = (val) => {
    loading.value = !!val;
};

const updateRawRows = (values) => {
    values = values.trim().split("\n");

    rows.value = values.map((value) => {
        return createRow({ title: value });
    });
};

defineExpose({ addRowsAt, removeRow, updateRow, replaceRows, getRows, setLoading });
</script>

<template>
    <div class="relative">
        <div v-if="isSimpleMode" class="space-y-6">
            <div class="p-4 bg-gray-50 rounded-md">
                <div class="flex">
                    <div class="flex-shrink-0">
                        <InformationCircleIcon class="w-5 h-5 text-gray-600" />
                    </div>
                    <div class="ml-3">
                        <h3 class="text-sm font-medium text-gray-900 dark:text-white">
                            {{ __("Mode simplifié") }}
                        </h3>
                        <div class="mt-2 text-sm text-gray-600 dark:text-gray-400">
                            <p>
                                {{ __("Ce mode est automatiquement activé lorsque le nombre de requêtes est supérieur à 1000.") }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <FormTextarea @update:modelValue="updateRawRows" :value="rows.map((row) => row.title).join('\n')" rows="30" />
        </div>
        <div v-else class="space-y-6">
            <div class="flex justify-center items-center space-x-3" v-if="_headerActions.length > 0">
                <ActionButton v-for="action in _headerActions" :action="action" @click="runAction(action)" />
            </div>
            <div>
                <div v-for="(row, index) in rows" ref="rowRefs">
                    <Row
                        v-model="rows[index]"
                        :index="index"
                        :key="row.id"
                        :depthEnabled="depthEnabled"
                        :sortableEnabled="sortableEnabled"
                        :actions="actions.row"
                        :readonly="readonly"
                        :disableNewRows="disableNewRows"
                        @focus="isFocused = true"
                        @blur="isFocused = false"
                        @enterKeyPressed="enterKeyPressed(index)"
                        @tabKeyPressed="tabKeyPressed(index)"
                        @shiftedTabKeyPressed="shiftedTabKeyPressed(index)"
                        @deleteKeyPressed="deleteKeyPressed($event, index)"
                        @pastePressed="pastePressed($event, index)"
                        @fallbackKeyPressed="fallbackKeyPressed(index)"
                        @upKeyPressed="upKeyPressed(index)"
                        @downKeyPressed="downKeyPressed(index)"
                        @removeRow="removeRow(index)"
                        @moveUpPressed="moveUpPressed(index)"
                        @moveDownPressed="moveDownPressed(index)"
                        :placeholder="__('Saisissez un titre de publication')"
                        :class="[
                            index === 0 ? 'rounded-t-md border-t' : '',
                            index === rows.length - 1 ? 'rounded-b-md border-b' : '',
                            isFocused ? 'border-gray-950 dark:border-gray-700' : 'border-gray-300 dark:border-gray-800',
                            'border-r border-l overflow-hidden',
                        ]" />
                </div>
            </div>
            <div class="flex justify-center items-center space-x-3" v-if="_footerActions.length > 0">
                <ActionButton v-for="action in _footerActions" :action="action" @click="runAction(action)" />
            </div>
        </div>
        <div v-if="loading" class="bg-opacity-60 absolute inset-0 flex justify-center items-center bg-white dark:bg-gray-900">
            <LoadingAnimation class="w-16 h-16" />
        </div>
    </div>
</template>
